import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { clone } from "../../../features/utils/objClone";
import mergeDeep from "../../../utils/object/mergeDeep";
import { set } from "lodash";

export const initTemplateState = {
  layoutType: 1,
  logoImage: null,
  primaryColor: "#000000",
  secondaryColor: "#1E61DC",
  buttonColor: "#1E61DC",
  fontFamily: "Arial, sans-serif",
  headerStyle: "1",
  listingStyle: "1",
  searchbarStyle: "2",
  mobileSearchbarStyle: "1",
  footerStyle: "1",
  selectedPages: ["about", "contact", "t&c", "destination", "policy", "faq"],
  offerFilterStyle: "1",
  flightOfferSort: "1",
  aboutLayout: 1, // 0 = hidden, 1 = visible
  contactLayout: 1, // 0 = hidden, 1 = visible
  dealsLayout: 1, // 0 = hidden, 1 = visible
  termsLayout: 1, // 0 = hidden, 1 = visible
  testimonialsLayout: 1, // 0 = hidden, 1 = visible
  privacyPolicyLayout: 1, // 0 = hidden, 1 = visible
  landingHeroLayout : 1,
  activeProducts: ["all"],
  paymentMethods: ["all"],
  heroStyle: "1",
  pagesVisibility: {
    home: true,
    deals: true,
    about: true,
    contact: true,
    testimonial: true,
    flight: true,
    hotel: true,
    tour: true,
    terms: true,
    privacypolicy: true,
    faq: true,
  },
  homeLayoutSection: {
    flight: { isVisible: true, layout: "1", title: "Flight Booking" },
    hotel: { isVisible: true, layout: "1", title: "Hotel Reservations" },
    tour: { isVisible: true, layout: "1", title: "Tour Packages" },
    deals: { isVisible: true, layout: "1", title: "Exclusive Deals" },
    testimonial: {
      isVisible: true,
      layout: "1",
      title: "Customer Testimonials",
    },
    faq: { isVisible: true, layout: "1", title: "Frequently Asked Questions" },
    adGrid: { isVisible: true, layout: "1", title: "Advertisements" },
    ourServices: { isVisible: true, layout: "1", title: "Our Services" },
    banner: { isVisible: true, layout: "1", title: "Promotional Banner" },
    quickInfo: { isVisible: true, layout: "1", title: "Quick Information" },
  },
  heroImageStyle: "cover", // contain, cover, auto
  homeLayoutStyle: "boxed", // boxed, full-width
};

let initialState = clone(initTemplateState);

let UDStorage = localStorage.getItem("userData");
if (UDStorage) {
  try {
    const parsedData = JSON.parse(UDStorage);
    const savedState = parsedData?.crmData?.templateData;
    // const savedState = parsedData?.user?.detail?.templateData;
    if (savedState) {
      if (typeof savedState === "object") {
        // initialState = {...initialState, ...savedState }; // Merge saved state
        mergeDeep(initialState, savedState);
      }
      // initialState = {...initialState, ...JSON.parse(savedState) }; // Merge saved state
      else mergeDeep(initialState, JSON.parse(savedState));
    }
  } catch (error) {
    console.error("Error parsing userData from localStorage:", error);
  }
}

const templateSlice = createSlice({
  name: "template",
  initialState,
  reducers: {
    setLogoImage(state, action) {
      state.logoImage = action.payload;
    },
    setPrimaryColor(state, action) {
      state.primaryColor = action.payload;
    },
    setSecondaryColor(state, action) {
      state.secondaryColor = action.payload;
    },
    setFontFamily(state, action) {
      state.fontFamily = action.payload;
    },
    setHeaderStyle(state, action) {
      state.headerStyle = action.payload;
    },
    setListingStyle(state, action) {
      state.listingStyle = action.payload;
    },
    setSearchbarStyle(state, action) {
      state.searchbarStyle = action.payload;
    },
    setFooterStyle(state, action) {
      state.footerStyle = action.payload;
    },
    setOfferFilterStyle(state, action) {
      state.offerFilterStyle = action.payload;
    },
    setFlightOfferSort(state, action) {
      state.flightOfferSort = action.payload;
    },
    setSelectedPages(state, action) {
      const id = action.payload;
      if (state.selectedPages.includes(id)) {
        state.selectedPages = state.selectedPages.filter(
          (pageId) => pageId !== id
        );
      } else {
        state.selectedPages.push(id);
      }
    },
    setResetState(state) {
      Object.assign(state, clone(initTemplateState)); // Reset to initial state
    },
    setTemplate(state, action) {
      // return {
      //   ...state,
      //   ...action.payload,
      // };
      return mergeDeep(state, action.payload);
    },
    setActiveProducts(state, action) {
      state.activeProducts = action.payload;
    },
    setPaymentMethods(state, action) {
      state.paymentMethods = action.payload;
    },
    setHeroStyle(state, action) {
      state.heroStyle = action.payload;
    },
    setAboutLayout(state, action) {
      state.aboutLayout = action.payload;
    },
    setContactLayout(state, action) {
      state.contactLayout = action.payload;
    },
    setDealsLayout(state, action) {
      state.dealsLayout = action.payload;
    },
    setTermsLayout(state, action) {
      state.termsLayout = action.payload;
    },
    setLandingHeroLayout(state, action) {
      state.landingHeroLayout = action.payload;
    },
    setTestimonialsLayout(state, action) {
      state.testimonialsLayout = action.payload;
    },
    setPrivacyPolicyLayout(state, action) {
      state.privacyPolicyLayout = action.payload;
    },
    togglePageVisibility(state, action) {
      const pageId = action.payload;
      if (state.pagesVisibility[pageId] !== undefined) {
        state.pagesVisibility[pageId] = !state.pagesVisibility[pageId];
      } else {
        console.warn(`Page ID ${pageId} not found in pagesVisibility`);
      }
    },
    setPageVisibility(state, action) {
      const { page, visible } = action.payload;
      if (state.pagesVisibility[page] !== undefined) {
        state.pagesVisibility[page] = visible;
      } else {
        console.warn(`Page ID ${page} not found in pagesVisibility`);
      }
      console.log(state.pagesVisibility[page]);
    },

    setHomeLayoutSectionVisibility(state, action) {
      const { section, isVisible } = action.payload;

      if (
        state.homeLayoutSection[section] &&
        typeof state.homeLayoutSection[section] === "object"
      ) {
        state.homeLayoutSection[section].isVisible = isVisible;
      } else {
        console.warn(
          `Section ${section} not found or is not an object in homeLayoutSection`
        );
      }
    },
    setHomeLayoutSectionType(state, action) {
      const { section, layout } = action.payload;

      if (typeof layout !== "string") {
        console.warn(
          `Invalid layout type for ${section}: Expected string, got ${typeof layout}`
        );
        return;
      }

      if (state.homeLayoutSection?.[section]) {
        state.homeLayoutSection[section].layout = layout; // Immer allows direct mutation
      } else {
        console.warn(`Section "${section}" not found in homeLayoutSection`);
      }
    },

    setHomeLayoutSectionTitle(state, action) {
      const { section, title } = action.payload;

      if (typeof title !== "string") {
        console.warn(
          `Invalid title: Expected a string but received ${typeof title}`
        );
        return; // Stop execution if title is not a string
      }

      if (state.homeLayoutSection[section]) {
        state.homeLayoutSection[section].title = title;
      } else {
        console.warn(`Section ${section} not found in homeLayoutSection`);
      }
    },

    setHomeLayoutStyle(state, action) {
      const validStyles = ["boxed", "full-width"];
      if (validStyles.includes(action.payload)) {
        state.homeLayoutStyle = action.payload;
      } else {
        console.warn(`Invalid home layout style: ${action.payload}`);
      }
    },

    setHeroImageStyle(state, action) {
      const validStyles = ["contain", "cover", "auto"];
      if (validStyles.includes(action.payload)) {
        state.heroImageStyle = action.payload;
      } else {
        console.warn(`Invalid hero image style: ${action.payload}`);
      }
    },
  },

  setMobileSearchbarStyle(state, action) {
    state.mobileSearchbarStyle = action.payload;
  }


});

export const {
  setTemplate,
  setLogoImage,
  setPrimaryColor,
  setSecondaryColor,
  setFontFamily,
  setHeaderStyle,
  setListingStyle,
  setSearchbarStyle,
  setFooterStyle,
  setSelectedPages,
  setResetState,
  setOfferFilterStyle,
  setFlightOfferSort,
  setActiveProducts,
  setPaymentMethods,
  setHeroStyle,
  setAboutLayout,
  setContactLayout,
  setDealsLayout,
  setTermsLayout,
  setTestimonialsLayout,
  setPrivacyPolicyLayout,
  togglePageVisibility,
  setPageVisibility,
  setHomeLayoutSection,
  setHeroImageStyle,
  setHomeLayoutSectionVisibility,
  setHomeLayoutSectionType,
  setHomeLayoutStyle,
  setLandingHeroLayout,
  setMobileSearchbarStyle,
  setHomeLayoutSectionTitle, // Add the new action here
} = templateSlice.actions;

export default templateSlice.reducer;
